'use client';

import { useEffect, useState } from 'react';

import {
  gql,
  useMutation,
  ApolloQueryResult,
  OperationVariables,
} from '@nerdwallet/apollo';

import { useQueryWithLog } from '@lib/hooks/logging-hooks';
import { getLogger } from '@lib/logging';
import { usePrevious } from '@lib/utils';
import { NERDWALLET_PLUS_SHOPPING_REWARDS_QUERY } from '@app/(compass)/home/nerdwallet-plus/(dashboard)/components/shopping-rewards-trackers';

import {
  FinancialBankingRewardEvent,
  FinancialBankingReward,
  FinancialBankingRewardEnum,
  FinancialCreditCardRewardEnum,
  FinancialCreditCardRewardEvent,
  FinancialCreditCardReward,
} from '@generated-gql/generated-types';

import { isEmpty } from '@lib/utils';

const logger = getLogger('useNerdWalletPlusCelebrationModal');

const UPDATE_FINANCIAL_SHOPPING_REWARD = gql`
  mutation UpdateFinancialNwPlusRewardViewed($id: ID!, $eventId: ID!) {
    updateFinancialNwPlusRewardViewed(id: $id, eventId: $eventId) {
      creditCard {
        id
        events {
          id
          eventType
          emittedAt
          viewedAt
          pointsRewarded
        }
      }
      bankingAccount {
        id
        events {
          id
          eventType
          emittedAt
          viewedAt
          pointsRewarded
        }
      }
    }
  }
`;

const CELEBRATORY_EVENT_TYPES = [
  FinancialCreditCardRewardEnum.PaidEligibleCreditCardOnTime,
  FinancialBankingRewardEnum.MadeDirectDepositForEligibleBankAccount,
  FinancialBankingRewardEnum.SavingsAccountFunded,
];

interface ShoppingReward {
  shoppingId: string;
  event: FinancialCreditCardRewardEvent | FinancialBankingRewardEvent;
}

const extractRewardsToCelebrate = (
  rewardsToCelebrate: ShoppingReward[],
  shoppingId: string,
  events?: FinancialBankingRewardEvent[] | FinancialCreditCardRewardEvent[],
) => {
  events?.forEach((event) => {
    if (
      isEmpty(event.viewedAt) &&
      CELEBRATORY_EVENT_TYPES.includes(event.eventType) &&
      event.pointsRewarded > 0
    ) {
      rewardsToCelebrate.push({
        shoppingId,
        event,
      });
    }
  });
};

interface Props {
  showNwPlusCelebrationModal: (
    rewardedEvent: string,
    onClose: () => void,
    pointsEarned?: number,
  ) => void;
  skip?: boolean;
  effectiveAppDate: string;
}

type UseNerdWalletPlusShoppingRewards = (options: Props) => [
  (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<any>>,
  {
    nwPlusCreditCards: FinancialCreditCardReward[];
    nwPlusBankingAccounts: FinancialBankingReward[];
    isDataLoading: boolean;
  },
];

/* Retrieves NerdWallet+ Shopping rewards and displays celebration modals when points are earned */
const useNerdWalletPlusShoppingRewards: UseNerdWalletPlusShoppingRewards = ({
  showNwPlusCelebrationModal,
  effectiveAppDate,
}) => {
  const [updateFinancialShoppingReward] = useMutation(
    UPDATE_FINANCIAL_SHOPPING_REWARD,
  );
  const [rewardCelebrations, setRewardCelebrations] = useState<
    ShoppingReward[]
  >([]);
  const {
    data: nwPlusShoppingRewardsData,
    refetch: refetchNwPlusRewards,
    loading: isDataLoading,
  } = useQueryWithLog(logger, NERDWALLET_PLUS_SHOPPING_REWARDS_QUERY, {
    variables: {
      currentDate: effectiveAppDate,
      filter: { isUserDismissed: false, forRewardTrackers: true },
    },
    onCompleted: (response) => {
      // Queue up celebrations to be shown
      const newRewardCelebrations: ShoppingReward[] = [];
      response?.financialNwPlusRewards?.creditCards?.forEach((creditCard) => {
        extractRewardsToCelebrate(
          newRewardCelebrations,
          creditCard.id,
          creditCard.events,
        );
      });
      response?.financialNwPlusRewards?.bankingAccounts?.forEach((account) => {
        extractRewardsToCelebrate(
          newRewardCelebrations,
          account.id,
          account.events,
        );
      });
      setRewardCelebrations(newRewardCelebrations);
    },
  });

  const nwPlusCreditCards =
    nwPlusShoppingRewardsData?.financialNwPlusRewards?.creditCards ?? [];
  const nwPlusBankingAccounts =
    nwPlusShoppingRewardsData?.financialNwPlusRewards?.bankingAccounts ?? [];

  const firstRewardEvent = rewardCelebrations[0];
  const prevFirstRewardEvent = usePrevious(firstRewardEvent);
  const [currentRewardEvent, setCurrentRewardEvent] =
    useState<typeof firstRewardEvent>();
  const [prevRewardEvent, setPrevRewardEvent] =
    useState<typeof firstRewardEvent>();
  useEffect(() => {
    if (
      !!firstRewardEvent &&
      firstRewardEvent?.event.id !== prevFirstRewardEvent?.event.id
    ) {
      setCurrentRewardEvent(firstRewardEvent);
    }
  }, [currentRewardEvent, firstRewardEvent, prevFirstRewardEvent]);

  useEffect(() => {
    // Only want the celebration to appear while on the "My Money" or "Nerdwallet+" tab.
    if (
      currentRewardEvent &&
      currentRewardEvent?.event.id !== prevRewardEvent?.event.id
    ) {
      setPrevRewardEvent(currentRewardEvent);

      const { event: currentEvent, shoppingId: currentCardId } =
        currentRewardEvent;
      // Mark event as being seen
      updateFinancialShoppingReward({
        variables: {
          id: currentCardId,
          eventId: currentEvent.id,
        },
      });
      const handleCloseCelebrationModal = () => {
        // Dequeue the current event
        setRewardCelebrations(
          rewardCelebrations.filter(
            ({ event, shoppingId }) =>
              event.id !== currentEvent.id || shoppingId !== currentCardId,
          ),
        );
      };
      // Display celebration modal
      showNwPlusCelebrationModal(
        currentEvent.eventType,
        handleCloseCelebrationModal,
        currentEvent.pointsRewarded,
      );
    }
  }, [
    rewardCelebrations,
    currentRewardEvent,
    prevRewardEvent,
    updateFinancialShoppingReward,
    showNwPlusCelebrationModal,
  ]);

  return [
    refetchNwPlusRewards,
    { nwPlusCreditCards, nwPlusBankingAccounts, isDataLoading },
  ];
};

export default useNerdWalletPlusShoppingRewards;
